import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  ArrowBack as BackIcon,
  AutoAwesome as AiIcon,
  PictureAsPdf as PdfIcon,
  Description as DocxIcon,
  Code as MdIcon,
  FileDownload as DownloadIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import { tokens } from "@/tokens";
import { useGenerateFirstSaleNotificationLetterMutation } from "@/services/first-sale/first-sale-api";
import {
  downloadMarkdownAsPdf,
  downloadMarkdownAsDocx,
  downloadMarkdownAsFile,
} from "@/utils/markdownExport";

interface GenerateCBPPacketModalProps {
  open: boolean;
  onClose: () => void;
  caseData: any;
  supplier: any;
  documents: any[];
  validation: any;
}

const GenerateCBPPacketModal: React.FC<GenerateCBPPacketModalProps> = ({
  open,
  onClose,
  caseData,
  validation,
}) => {
  const [attorney, setAttorney] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientTitle, setRecipientTitle] = useState("Director");
  const [importerAddress, setImporterAddress] = useState("");
  const [iorNumber, setIorNumber] = useState("");
  const [letterContent, setLetterContent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const [generateLetter, { isLoading }] = useGenerateFirstSaleNotificationLetterMutation();

  const handleGenerate = async () => {
    setError(null);
    try {
      const result = await generateLetter({
        caseId: caseData._id,
        attorney: attorney || undefined,
        recipientName: recipientName || undefined,
        recipientTitle: recipientTitle || undefined,
        importerAddress: importerAddress || undefined,
        iorNumber: iorNumber || undefined,
      }).unwrap();
      setLetterContent(result.content);
    } catch (err: any) {
      setError(
        err?.message ||
          err?.data?.message ||
          "Failed to generate notification letter. Please try again."
      );
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(letterContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const baseFilename = `${caseData.caseNumber}-cbp-notification-letter`;

  const handleDownloadPdf = async () => {
    setDownloadAnchor(null);
    setExporting(true);
    try {
      await downloadMarkdownAsPdf(letterContent, baseFilename);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadDocx = () => {
    setDownloadAnchor(null);
    downloadMarkdownAsDocx(letterContent, baseFilename);
  };

  const handleDownloadMd = () => {
    setDownloadAnchor(null);
    downloadMarkdownAsFile(letterContent, baseFilename);
  };

  const handleClose = () => {
    setLetterContent("");
    setError(null);
    setCopied(false);
    onClose();
  };

  const generated = letterContent.length > 0;
  const showWarning = validation?.overallScore != null && validation.overallScore < 80;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={generated ? "md" : "sm"}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.heavy,
          color: tokens.text.primary,
          pb: 0.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {generated && (
            <IconButton size="small" onClick={() => setLetterContent("")} sx={{ mr: 0.5 }}>
              <BackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <AiIcon sx={{ fontSize: 20, color: tokens.brand.primary }} />
          <span>{generated ? "CBP Notification Letter" : "Generate CBP Notification Letter"}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {!generated ? (
          <>
            {showWarning && (
              <Box
                sx={{
                  bgcolor: tokens.status.warningBg,
                  border: `1px solid ${tokens.status.warning}30`,
                  borderRadius: tokens.radius.md,
                  p: 1.5,
                  mb: 2.5,
                  mt: 1,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <WarningIcon sx={{ fontSize: 16, color: tokens.status.warning, mt: 0.25 }} />
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                    Eligibility score is <strong>{validation.overallScore}%</strong> (below 80%
                    threshold). Review flagged items before submitting to CBP.
                  </Typography>
                </Stack>
              </Box>
            )}

            <Box
              sx={{
                bgcolor: tokens.status.infoBg,
                border: `1px solid ${tokens.status.info}20`,
                borderRadius: tokens.radius.md,
                p: 1.5,
                mb: 2.5,
                mt: showWarning ? 0 : 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <AiIcon sx={{ fontSize: 16, color: tokens.status.info, mt: 0.25 }} />
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                  AI will analyze all uploaded documents and validation results to generate a formal
                  CBP notification letter with exhibit references, legal analysis, and Nissho Iwai
                  factor review. This may take 60-90 seconds.
                </Typography>
              </Stack>
            </Box>

            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.75 }}>
                  IOR Number
                </Typography>
                <TextField fullWidth size="small" value={iorNumber} onChange={(e) => setIorNumber(e.target.value)} placeholder="e.g., 86-342430100"
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.75 }}>
                  Importer Address
                </Typography>
                <TextField fullWidth size="small" multiline rows={2} value={importerAddress} onChange={(e) => setImporterAddress(e.target.value)} placeholder="Full importer address"
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                />
              </Box>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.75 }}>
                    CBP Recipient Name
                  </Typography>
                  <TextField fullWidth size="small" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="e.g., Melvin Moreland"
                    sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.75 }}>
                    Title
                  </Typography>
                  <TextField fullWidth size="small" value={recipientTitle} onChange={(e) => setRecipientTitle(e.target.value)} placeholder="e.g., Director"
                    sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                  />
                </Box>
              </Stack>
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.75 }}>
                  Prepared By
                </Typography>
                <TextField fullWidth size="small" value={attorney} onChange={(e) => setAttorney(e.target.value)} placeholder="Attorney or preparer name"
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                />
              </Box>
            </Stack>

            {error && (
              <Box sx={{ bgcolor: tokens.status.dangerBg, border: `1px solid ${tokens.status.danger}30`, borderRadius: tokens.radius.md, p: 1.5, mt: 2 }}>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.danger }}>{error}</Typography>
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              mt: 1,
              border: `1px solid ${tokens.border.container}`,
              borderRadius: tokens.radius.md,
              bgcolor: tokens.bg.surface,
              p: 2.5,
              maxHeight: "60vh",
              overflow: "auto",
              fontSize: tokens.fontSize.sm2,
              lineHeight: 1.8,
              color: tokens.text.body,
              "& h1": { fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, mt: 2, mb: 1 },
              "& h2": { fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, mt: 2, mb: 0.5 },
              "& h3": { fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, mt: 1.5, mb: 0.5 },
              "& ul, & ol": { pl: 3, my: 0.5 },
              "& li": { mb: 0.25 },
              "& p": { mb: 1 },
              "& strong": { fontWeight: tokens.fontWeight.bold },
              "& table": { borderCollapse: "collapse", width: "100%", my: 1 },
              "& th, & td": { border: `1px solid ${tokens.border.default}`, px: 1.5, py: 0.75, fontSize: tokens.fontSize.xs, textAlign: "left" },
              "& th": { bgcolor: tokens.bg.hover, fontWeight: tokens.fontWeight.semibold },
            }}
          >
            <ReactMarkdown>{letterContent}</ReactMarkdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {!generated ? (
          <>
            <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.secondary, "&:hover": { bgcolor: tokens.bg.hover } }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AiIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              {isLoading ? "Generating..." : "Generate Letter"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.secondary, "&:hover": { bgcolor: tokens.bg.hover } }}>
              Close
            </Button>
            <Button
              startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
              onClick={handleCopy}
              variant="outlined"
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, color: tokens.text.primary,
                borderColor: tokens.border.default, borderRadius: tokens.radius.md, "&:hover": { bgcolor: tokens.bg.hover },
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
              variant="contained"
              disabled={exporting}
              onClick={(e) => setDownloadAnchor(e.currentTarget)}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              Download
            </Button>
            <Menu
              anchorEl={downloadAnchor}
              open={!!downloadAnchor}
              onClose={() => setDownloadAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: { borderRadius: tokens.radius.md, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 180 },
                },
              }}
            >
              <MenuItem onClick={handleDownloadPdf} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><PdfIcon sx={{ fontSize: 18, color: "#e53935" }} /></ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDownloadDocx} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><DocxIcon sx={{ fontSize: 18, color: "#1565c0" }} /></ListItemIcon>
                <ListItemText>Word (.doc)</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleDownloadMd} sx={{ fontSize: tokens.fontSize.sm2 }}>
                <ListItemIcon><MdIcon sx={{ fontSize: 18, color: "#6B7280" }} /></ListItemIcon>
                <ListItemText>Markdown (.md)</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateCBPPacketModal;
